<script setup>
import { defineProps, defineEmits, ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useIntegrationHook } from 'dashboard/composables/useIntegrationHook';
import { useBranding } from 'shared/composables/useBranding';
import { useAlert } from 'dashboard/composables';
import CaptainLlmCredentialAPI from 'dashboard/api/captain/llmCredential';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  integrationId: {
    type: String,
    required: true,
  },
});

defineEmits(['add', 'delete']);

const { t } = useI18n();
const { integration, hasConnectedHooks } = useIntegrationHook(
  props.integrationId
);

const { replaceInstallationName } = useBranding();

const isTesting = ref(false);
const canTestCredential = computed(
  () => props.integrationId === 'openai' && hasConnectedHooks.value
);

const testCredential = async () => {
  isTesting.value = true;
  try {
    const { data } = await CaptainLlmCredentialAPI.test();
    if (data.ok) {
      useAlert(t('INTEGRATION_APPS.TEST_CONNECTION.SUCCESS'));
    } else {
      useAlert(
        t('INTEGRATION_APPS.TEST_CONNECTION.ERROR', { error: data.error })
      );
    }
  } catch (error) {
    const message = error?.response?.data?.error || error?.message;
    useAlert(t('INTEGRATION_APPS.TEST_CONNECTION.ERROR', { error: message }));
  } finally {
    isTesting.value = false;
  }
};
</script>

<template>
  <div
    class="outline outline-n-container outline-1 bg-n-card rounded-xl flex-grow overflow-auto p-4"
  >
    <div class="flex items-center justify-center">
      <div class="flex h-16 w-16 items-center justify-center">
        <img
          :src="`/dashboard/images/integrations/${integrationId}.png`"
          class="max-w-full rounded-md border border-n-weak shadow-sm block dark:hidden bg-n-alpha-3 dark:bg-n-alpha-2"
        />
        <img
          :src="`/dashboard/images/integrations/${integrationId}-dark.png`"
          class="max-w-full rounded-md border border-n-weak shadow-sm hidden dark:block bg-n-alpha-3 dark:bg-n-alpha-2"
        />
      </div>
      <div class="flex flex-col justify-center m-0 mx-4 flex-1">
        <h3 class="mb-1 text-heading-1 text-n-slate-12">
          {{ integration.name }}
        </h3>
        <p class="text-n-slate-11 text-body-main">
          {{ replaceInstallationName(integration.description) }}
        </p>
      </div>
      <div class="flex justify-center items-center mb-0 w-[15%]">
        <div v-if="hasConnectedHooks" class="flex items-center gap-2">
          <Button
            v-if="canTestCredential"
            faded
            slate
            :is-loading="isTesting"
            :label="$t('INTEGRATION_APPS.TEST_CONNECTION.BUTTON_TEXT')"
            @click="testCredential"
          />
          <div @click="$emit('delete', integration.hooks[0])">
            <Button
              ruby
              faded
              :label="$t('INTEGRATION_APPS.DISCONNECT.BUTTON_TEXT')"
            />
          </div>
        </div>
        <div v-else>
          <Button
            blue
            faded
            :label="$t('INTEGRATION_APPS.CONNECT.BUTTON_TEXT')"
            @click="$emit('add')"
          />
        </div>
      </div>
    </div>
  </div>
</template>
